import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "@/components/ui/use-toast";
import { api } from "@/lib/api";
import { formatWallDate } from "@/lib/datetime";
import { formatNumber } from "@/lib/i18n";
import { useRowMutation } from "@/lib/use-api-mutation";
import { useState } from "react";
import { useTranslation } from "react-i18next";

/**
 * "Send to existing report" — pours a ticked selection (time entries, or
 * fixed-price work items) of a single customer into one of that
 * customer's open DRAFT reports.
 *
 * Only drafts are listed: a sent / paid report is already on a real
 * Fakturoid invoice, so growing it would silently desync the two. The
 * server enforces the same-customer + billing-state contract; this
 * dialog is just the picker. The accountant clicks a draft, the items
 * are appended, and the list refreshes via `onDone`.
 */

type DraftBatch = {
  id: string;
  periodFrom: string;
  periodTo: string;
  totalAmount: string;
  _count: { items: number };
};

type Props = {
  /** What is being sent — decides the endpoint and the wording. */
  kind: "entries" | "work-items";
  ids: string[];
  customerId: string;
  customerName: string | null;
  /** Called after the items are successfully appended — clears selection
   *  and reloads the listing. */
  onDone: () => void;
  /** Trigger element — the bulk bar's "Send to existing report" button. */
  children: React.ReactNode;
};

const KIND = {
  entries: { endpoint: "add-entries", body: "timeEntryIds" },
  "work-items": { endpoint: "add-work-items", body: "workItemIds" },
} as const;

export function AddToDraftReportDialog({
  kind,
  ids,
  customerId,
  customerName,
  onDone,
  children,
}: Props) {
  const { t } = useTranslation();
  const { endpoint, body } = KIND[kind];
  // Key context for the entries / work-items wording (`…_entries_one`, `…_workItems_few`, …).
  const context = kind === "entries" ? "entries" : "workItems";
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [drafts, setDrafts] = useState<DraftBatch[] | null>(null);

  async function loadDrafts() {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        customerId,
        status: "draft",
        pageSize: "200",
      });
      const r = await api.get<{ batches: DraftBatch[] }>(`/api/billing?${params.toString()}`);
      setDrafts(r.batches);
    } catch (err) {
      toast({
        title: t("billing.addToDraft.loadFailed"),
        description: err instanceof Error ? err.message : t("billing.addToDraft.error"),
        variant: "destructive",
      });
      setDrafts([]);
    } finally {
      setLoading(false);
    }
  }

  const send = useRowMutation(
    (batchId: string) =>
      api.post<{ added: number }>(`/api/billing/${batchId}/${endpoint}`, { [body]: ids }),
    {
      rowKey: (batchId) => batchId,
      onSuccess: ({ added }) => {
        toast({
          title: t("billing.addToDraft.added", { count: added, context }),
          variant: "success",
        });
        setOpen(false);
        onDone();
      },
      errorMessage: (err) => (err instanceof Error ? err.message : t("billing.addToDraft.error")),
      onError: (msg) =>
        toast({
          title: t("billing.addToDraft.addFailed"),
          description: msg,
          variant: "destructive",
        }),
    },
  );

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) void loadDrafts();
      }}
    >
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t("billing.addToDraft.title")}</DialogTitle>
          <DialogDescription>
            {t("billing.addToDraft.description", { count: ids.length, context })}
            {customerName
              ? ` ${t("billing.addToDraft.forCustomer", { customer: customerName })}`
              : ""}
            .
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            {t("billing.addToDraft.loading")}
          </p>
        ) : drafts && drafts.length > 0 ? (
          <ul className="max-h-80 space-y-2 overflow-y-auto">
            {drafts.map((b) => (
              <li key={b.id}>
                <button
                  type="button"
                  disabled={send.busy}
                  onClick={() => send.run(b.id)}
                  className="flex w-full items-center justify-between gap-3 rounded-md border bg-card px-3 py-2 text-left text-sm transition-colors hover:bg-accent disabled:opacity-50"
                >
                  <span>
                    <span className="font-medium">
                      {formatWallDate(b.periodFrom)} – {formatWallDate(b.periodTo)}
                    </span>
                    <span className="text-muted-foreground">
                      {" · "}
                      {t("billing.addToDraft.lines", { count: b._count.items })}
                    </span>
                  </span>
                  <span className="whitespace-nowrap text-muted-foreground">
                    {send.isRunning(b.id)
                      ? t("billing.addToDraft.adding")
                      : `${formatNumber(Number(b.totalAmount), undefined, {})} Kč`}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="py-6 text-center text-sm text-muted-foreground">
            {t("billing.addToDraft.empty")}
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
}
